class ListingView {
  constructor() {
    this.listingViewSelector = document.querySelector(
      "listing-view-selector[data-listing-view]",
    );
    if (!this.listingViewSelector) return;

    this.listingViewButtons = Array.from(
      this.listingViewSelector.querySelectorAll(".listing-view-selector__button"),
    );
    // spotlight (1 full-width + 2x2 + 2x2) is deliberately absent from
    // selectableViews: it has no button, so it can only ever arrive as the
    // collection's server-rendered default. The moment the customer picks
    // anything else we store that choice, and since spotlight can never be stored
    // it cannot come back until the session ends.
    this.renderableViews = ["spotlight", "standard", "single", "grid"];
    this.selectableViews = ["standard", "single", "grid"];
    this.fallbackView = "standard";

    this.mobileImageSizes = {
      spotlight: "(min-width: 481px) calc(50vw - 1rem), calc(50vw - 0.5rem)",
      standard: "(min-width: 481px) calc(50vw - 1rem), calc(50vw - 0.5rem)",
      single: "(min-width: 481px) calc(100vw - 2rem), calc(100vw - 1rem)",
      grid: "(min-width: 481px) calc(25vw - 1.25rem), calc(25vw - 0.625rem)",
    };
    // In spotlight every 5th card spans the full row, so it needs full-width
    // images rather than the half-width default.
    this.fullSlotImageSizes = this.mobileImageSizes.single;

    // The preference is per-collection and lasts only for the browsing
    // session: closing the site drops it and the merchant's per-collection
    // default takes over again on the next visit.
    //
    // The "plpView:" prefix retires entries written under the earlier naming,
    // where "grid" meant the 2-column view rather than the 4-column one. Those
    // values are still syntactically valid, so without a new prefix a customer
    // mid-session would silently land in the wrong view.
    this.storageKey = "plpView:" + this.getScope();
    this.clearLegacyPreference();

    this.registerEvents();
  }

  // Older builds stored a single global preference in localStorage, which
  // survived browser restarts and overrode every collection's default
  // forever. Drop it so nobody stays stuck on a stale choice.
  clearLegacyPreference() {
    try {
      localStorage.removeItem("listingView");
    } catch (error) {}
  }

  getScope() {
    const productGrid = document.querySelector("#product-grid");
    const scope = productGrid && productGrid.getAttribute("data-listing-view-scope");
    return scope || window.location.pathname;
  }

  // The server default is the only path spotlight can arrive through, so it's
  // validated against renderableViews rather than selectableViews.
  getDefaultView() {
    const productGrid = document.querySelector("#product-grid");
    const configured = productGrid
      ? productGrid.getAttribute("data-default-listing-view")
      : null;
    return this.renderableViews.indexOf(configured) > -1
      ? configured
      : this.fallbackView;
  }

  registerEvents() {
    this.listingViewSelector.addEventListener("click", (event) => {
      const button = this.getViewButton(event.target);
      if (!button) return;

      const view = this.normalizeView(button.dataset.view);
      try {
        sessionStorage.setItem(this.storageKey, view);
      } catch (error) {}

      this.applyView(view, { notify: true });
    });

    this.applyView();

    document.addEventListener("product-grid-updated", () => {
      this.applyView(this.getStoredView(), { notify: true });
    });
  }

  getViewButton(target) {
    let node = target;
    while (node && node !== this.listingViewSelector) {
      if (
        node.classList &&
        node.classList.contains("listing-view-selector__button") &&
        this.listingViewSelector.contains(node)
      ) {
        return node;
      }
      node = node.parentNode;
    }

    return null;
  }

  // Used for anything the customer can act on. spotlight is not selectable, so a
  // stale or hand-edited spotlight in storage collapses to the fallback instead of
  // resurrecting the hidden view.
  normalizeView(view) {
    return this.selectableViews.indexOf(view) > -1 ? view : this.fallbackView;
  }

  getStoredView() {
    let stored = null;
    try {
      stored = sessionStorage.getItem(this.storageKey);
    } catch (error) {}

    // No explicit choice this session — fall back to the collection default
    // the server rendered rather than to a hardcoded view.
    if (!stored) return this.getDefaultView();

    return this.normalizeView(stored);
  }

  updateImageSizes(productGrid, view) {
    const defaultSizes =
      this.mobileImageSizes[view] || this.mobileImageSizes[this.fallbackView];

    Array.prototype.forEach.call(productGrid.querySelectorAll("img[sizes]"), (img) => {
      const currentSizes = img.getAttribute("sizes") || "";
      const desktopSizes = currentSizes.indexOf("(min-width: 990px) 1px") > -1
        ? "(min-width: 990px) 1px"
        : "(min-width: 990px) calc((100vw - 6rem) / 4)";

      let mobileSizes = defaultSizes;
      if (view === "spotlight") {
        const item = img.closest("#product-grid > li");
        if (item && item.getAttribute("data-plp-slot") === "full") {
          mobileSizes = this.fullSlotImageSizes;
        }
      }

      img.setAttribute("sizes", `${desktopSizes}, ${mobileSizes}`);
    });
  }

  applyView(view = this.getStoredView(), options = {}) {
    // Renderable, not selectable — applyView has to be able to paint spotlight
    // when it's the collection default. While spotlight is showing, none of the
    // three buttons is active, which is accurate: the customer is in a view
    // none of them represents.
    const currentView =
      this.renderableViews.indexOf(view) > -1 ? view : this.fallbackView;
    const productGrid = document.querySelector("#product-grid");
    const previousView = productGrid ? productGrid.getAttribute("data-listing-view") : null;

    if (productGrid) {
      productGrid.setAttribute("data-listing-view", currentView);
      this.updateImageSizes(productGrid, currentView);
    }

    this.listingViewButtons.forEach((button) => {
      button.classList.toggle("is-active", button.dataset.view === currentView);
    });

    if (options.notify && productGrid && previousView !== currentView) {
      document.dispatchEvent(
        new CustomEvent("listing-view-changed", {
          detail: { view: currentView, previousView },
        }),
      );
    }
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => new ListingView());
} else {
  new ListingView();
}
